import { Button, Grid, Menu, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import CustomTooltip from "../../../../components/CustomTooltip";
import MultiSelectWithAll from "../../../../components/MultiSelectWithAll";
import SpinningIcon from "../../../../components/SpinningIcon";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import { NotificationContext } from "../../../../contexts/NotificationContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { entityTypes } from "../../../../utils/constants";
import { EntityTypeInfo } from "../../../stack/environment/components/constants";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";

const CollectEntitiesButton = ({
  loadingMessage,
  setLoading,
  children,
  ...props
}) => {
  const { isLoading, setIsLoading, setIsScanning, setTaskId } =
    useContext(NotificationContext);
  const colors = useColors();
  const [anchorEl, setAnchorEl] = useState(null);

  const collectMessage = "Collecting in the background...";

  const { runEntityCollection } = useAPI();

  const [selectedEntityTypes, setSelectedEntityTypes] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [collectableTools, setCollectableTools] = useState([]);
  const [openDialogForCollectTools, setOpenDialogForCollectTools] =
    useState(false);
  const { integrations, isIntegratedLoading } = useIsIntegrated();

  const entityTypeOptions = entityTypes().map((entityType) => ({
    key: entityType.type,
    name: entityType.name,
    icon: EntityTypeInfo[entityType.type]?.icon ? (
      <SvgToIcon IconLogo={EntityTypeInfo[entityType.type].icon} width="20px" />
    ) : null,
  }));

  const orderedTools = [...collectableTools].sort();
  const integrationOptions = orderedTools.map((tool) => ({
    key: tool,
    name: IntegrationsMapping[tool]?.displayName,
    icon: IntegrationsMapping[tool]?.logo ? (
      <SvgToIcon IconLogo={IntegrationsMapping[tool].logo} width="32px" />
    ) : null,
  }));

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  useEffect(() => {
    if (integrations && !isIntegratedLoading) {
      let filteredTools = integrations.filter(
        (tool) => IntegrationsMapping[tool]?.collectable
      );
      filteredTools.push("breeze");
      setCollectableTools(filteredTools);
    }
  }, [integrations, isIntegratedLoading]);

  const requestCollection = () => {
    setIsLoading(true);
    runEntityCollection(selectedTools, selectedEntityTypes).then((result) => {
      setIsScanning(true);
      Toast.info(collectMessage);
      setTaskId(result.data);
    });
  };

  const handleCollectMenuClick = async (event) => {
    if (!isLoading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCollectMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCollectClick = () => {
    setOpenDialogForCollectTools(true);
  };

  const handleDialogClose = () => {
    setOpenDialogForCollectTools(false);
  };

  const handleConfirm = () => {
    if (openDialogForCollectTools) {
      if (selectedTools.length > 0 && selectedEntityTypes.length > 0) {
        requestCollection();
        handleCollectMenuClose();
      }
    }
    handleDialogClose();
  };

  return (
    <>
      <CustomTooltip title={isLoading ? loadingMessage : ""}>
        <span>
          <Button
            sx={{
              background: colors.grey_50,
              color: colors.contrast_text_dark,
              height: "32px",
              marginLeft: "5px",
              border: "1px solid",
              borderRadius: "8px",
            }}
            startIcon={<SpinningIcon isLoading={isLoading} />}
            onClick={handleCollectMenuClick}
            disabled={isLoading}
            {...props}
            style={{ textTransform: "none" }}
          >
            {children}
          </Button>
        </span>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleCollectMenuClose}
        >
          <Grid container spacing={2} ml={"10px"}>
            <Grid item xs={6}>
              <Typography variant="subtitle1">Entity Types</Typography>
              <MultiSelectWithAll
                options={entityTypeOptions}
                onSelectionChange={setSelectedEntityTypes}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1">Tools</Typography>
              <MultiSelectWithAll
                options={integrationOptions}
                onSelectionChange={setSelectedTools}
              />
            </Grid>
          </Grid>
          <PrimaryButton
            sx={{ margin: "auto", display: "block" }}
            onClick={handleCollectClick}
            disabled={
              selectedTools.length === 0 || selectedEntityTypes.length === 0
            }
          >
            Collect
          </PrimaryButton>
        </Menu>
      </CustomTooltip>
      <ConfirmationDialog
        openDialog={openDialogForCollectTools}
        handleDialogClose={handleDialogClose}
        handleConfirm={handleConfirm}
        dialogContentFirstLine="Are you sure you want to start collection?"
        dialogContentSecondLine="This action can take a few minutes."
        confirmationButtonText="Collect"
      />
    </>
  );
};

export default CollectEntitiesButton;
